/*
 * Copyright (c) 2026 CalAmp Corp. All Rights Reserved
 */
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import {CellMouseOverEvent, ColDef, ColumnState, FilterChangedEvent} from 'ag-grid-community';
import {translate} from '@ion/core/src/locale/locale';
import {CTCEntity, Entity, GenericObject} from '@ion/core/src/types/entities';
import {OfflineCanRequestResult, OfflineCanRequestStatus} from '@ion/core/src/types/services';
import {formatDateTime} from '@ion/core/src/utils/DateUtil';
import AuditDialog from '@ion/dls/src/editors/audit/AuditDialog';
import ViewAuditChangesDialog from '@ion/dls/src/editors/audit/ViewAuditChangesDialog';
import {resolveDefinitions} from '@ion/dls/src/grid/ColumnResolver';
import {HeaderButton} from '@ion/dls/src/grid/HeaderButton';
import IonGrid, {
  AgGridType,
  IonGridAPI,
  RowModelTypes,
  RowSelectionConfig
} from '@ion/dls/src/grid/IonGrid';
import {PAGE_SIZES} from '@ion/dls/src/grid/settings';
import {useColumnChooser} from '@ion/dls/src/grid/useColumnChooser';
import {useColumnState} from '@ion/dls/src/grid/useColumnState';
import {RetryIcon} from '@ion/dls/src/icons/RetryIcon';
import Render from '@ion/dls/src/render/Render';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import CanRequestRowEditor from './CanRequestRowEditor';
import {canRetryCanRequest} from './CanRequestUtils';
import {SCOUT_AUDIT_OPTIONS} from './ScoutAuditUtils';

const BULK_TRANSLATIONS = 'vehicle-manager:vehicleHistoryGrid.canRequestsGrid.bulk';

/*
 * AG Grid's native `rowSelection` (checkboxes: true) auto-generates the
 * selection column under this fixed colId — it's not one of our own
 * columnDefinitions, so it can't carry a tooltipValueGetter.
 */
const SELECTION_COLUMN_ID = 'ag-Grid-SelectionColumn';

export interface CanRequestsGridProps {
  canRequestsData?: OfflineCanRequestResult[];
  loadItems?: (params: {
    page?: number;
    pageSize?: number;
    sort?: string[] | undefined;
    filter?: GenericObject;
    callback: Function;
  }) => void;
  refresh?: boolean;
  pageSize?: number;
  setGridPageSize?: (pageSize: number) => void;
  onViewDetails?: (request: OfflineCanRequestResult) => void;
  onViewJson?: (request: OfflineCanRequestResult) => void;
  onRetry?: (request: OfflineCanRequestResult) => void;
  onCancel?: (request: OfflineCanRequestResult) => void;
  retryingRequestId?: string | null;
  cancellingRequestId?: string | null;
  loading?: boolean;
  standalone?: boolean;
  columnState?: ColumnState[] | null;
  filterModel?: any;
  onColumnsUpdated?: (columns: ColumnState[]) => void;
  onFilterChanged?: (event: FilterChangedEvent) => void;
  omitHeader?: boolean;
  selectionEnabled?: boolean;
  selectedCount?: number;
  maxSelection?: number;
  bulkRetrying?: boolean;
  onSelectionChange?: (requests: OfflineCanRequestResult[]) => void;
  onBulkRetry?: () => void;
  canViewAuditLog?: boolean;
}

interface VeScoutAuditItem extends CTCEntity {
  esn?: string;
}

const getStatusStyles = () => ({
  [OfflineCanRequestStatus.Completed]  : {backgroundColor: dlsColor('caGreen')},
  [OfflineCanRequestStatus.Pending]    : {backgroundColor: dlsColor('caBlue')},
  [OfflineCanRequestStatus.Cancelling] : {backgroundColor: dlsColor('caOrange')},
  [OfflineCanRequestStatus.Retrying]   : {backgroundColor: dlsColor('tealBlue')},
  [OfflineCanRequestStatus.Failed]     : {backgroundColor: dlsColor('caRed')},
  [OfflineCanRequestStatus.Cancelled]  : {backgroundColor: dlsColor('grey1')}
});

const getDefaultStatusStyle = () => ({backgroundColor: dlsColor('grey3')});

const getCanRequestRowId = (params: any): string => `${params?.data?.requestId || ''}`;

const statusCell = (data) => {
  const status = data?.value || '';
  const style = getStatusStyles()[status] || getDefaultStatusStyle();
  return (
    <span style={{
      ...style,
      ...dlsAlt('typography', 'cta2'),
      fontSize     : '10px',
      color        : dlsColor('white'),
      borderRadius : '2px',
      display      : 'inline-block',
      padding      : '6px 10px',
      lineHeight   : 1
    }}>
      {status}
    </span>
  );
};

/**
 * Renders the CAN requests list grid for the selected vehicle context.
 *
 * The grid displays request-level metadata, status badges, and row menu actions
 * used to open request details or trigger retry or cancel for eligible rows.
 * When `selectionEnabled` is set, it also enables ag-grid's native multi-row
 * selection (checkbox column, header checkbox with the "select on page /
 * select all / deselect all" menu, and the dark bulk-selection bar), matching
 * the same pattern used by `PromoteToProductionGrid` elsewhere in the Profile
 * Manager view modal, with a "RETRY" action in place of the default bulk
 * actions button. Selection eligibility (`FAILED`/`COMPLETED`/`CANCELLED`
 * only) is always enforced via `isRowSelectable`; the `maxSelection` cap
 * itself is left to the caller (`Number.MAX_SAFE_INTEGER` disables it),
 * since only a single-device context can cheaply know how many PENDING
 * slots that device has left — a same-tick safety net trims any selection
 * burst (e.g. from "select all") back down to whatever cap is in effect.
 *
 * ```typescript
 * import CanRequestsGrid from
 *   '@ion/applications/src/pages/vehicle/manager/CanRequestsGrid';
 * ...
 *
 * <CanRequestsGrid canRequestsData={requests}
 *   loading={isLoading}
 *   retryingRequestId={retryingRequestId}
 *   cancellingRequestId={cancellingRequestId}
 *   onViewDetails={onViewDetails}
 *   onRetry={onRetry}
 *   onCancel={onCancel}
 *   selectionEnabled
 *   selectedCount={selectedCount}
 *   onSelectionChange={onSelectionChange}
 *   onBulkRetry={onBulkRetry} />
 * ```
 *
 * @param   canRequestsData The list of CAN requests to render as grid rows.
 * @param   onViewDetails Invoked when the user opens row details.
 * @param   onRetry Invoked when the user retries a request.
 * @param   onCancel Invoked when the user cancels a request.
 * @param   retryingRequestId The request id currently executing retry.
 * @param   cancellingRequestId The request id currently executing cancel.
 * @param   loading Determines whether the grid loading state is displayed.
 * @param   selectionEnabled Enables the checkbox column and bulk bar.
 * @param   selectedCount Number of requests currently selected.
 * @param   maxSelection Maximum number of requests selectable at once; pass
 *          `Number.MAX_SAFE_INTEGER` to disable the cap.
 * @param   bulkRetrying Determines whether the bulk retry action is in flight.
 * @param   onSelectionChange Invoked with the current eligible selection.
 * @param   onBulkRetry Invoked when the user triggers the bulk retry action.
 * @returns The rendered CAN requests client-side grid.
 */
const CanRequestsGrid = forwardRef<IonGridAPI, CanRequestsGridProps>(({
  canRequestsData,
  loadItems,
  refresh,
  pageSize,
  setGridPageSize,
  onViewDetails,
  onViewJson,
  onRetry,
  onCancel,
  retryingRequestId,
  cancellingRequestId,
  loading,
  filterModel,
  onColumnsUpdated,
  onFilterChanged,
  standalone = false,
  columnState = null,
  omitHeader = true,
  selectionEnabled = false,
  selectedCount = 0,
  maxSelection = Number.MAX_SAFE_INTEGER,
  bulkRetrying = false,
  onSelectionChange,
  onBulkRetry,
  canViewAuditLog = false
}, ref) => {
  const gridRef      = useRef<IonGridAPI>(null);
  const gridApiRef   = useRef<AgGridType | null>(null);
  const gridPageSize = pageSize || 20;
  const totalRows    = canRequestsData?.length || 0;
  const rowModelType = loadItems ? RowModelTypes.Infinite : RowModelTypes.ClientSide;
  const [auditLogEsn, setAuditLogEsn]                     = useState<string | null>(null);
  const [auditLogChangesRecord, setAuditLogChangesRecord] = useState<any>(null);

  const {definitions, defaultDefinitions} = useColumnState({
    columnDefinitions : getColumnDefinitions(standalone),
    columnState
  });

  const {
    showColumnChooser,
    columnDefinitions,
    onColumnChooserSave,
    toggleColumnChooser
  } = useColumnChooser(definitions);

  const onToggleColumnChooser = useCallback(() => {
    toggleColumnChooser(true);
  }, [toggleColumnChooser]);

  const onDeselectAll = useCallback(() => {
    gridApiRef.current?.deselectAll();
  }, []);

  useImperativeHandle(ref, () => ({
    onToggleColumnChooser,
    deselectAll : onDeselectAll
  }) as IonGridAPI, [onToggleColumnChooser, onDeselectAll]);

  const constructGridRows = useCallback((requests: OfflineCanRequestResult[] = []) => {
    return requests.map((item) => ({
      ...item,
      esn       : item?.esn || '',
      requestId : item?.requestId,
      label     : item?.label || '',
      createdBy : item?.createdBy || '',
      createdOn : item?.createdOn ? formatDateTime(item.createdOn) : '',
      updatedOn : item?.updatedOn ? formatDateTime(item.updatedOn) : '',
      status    : item?.status
    }));
  }, []);

  const onShowAuditLog = useCallback((request: OfflineCanRequestResult) => {
    if (canViewAuditLog && request?.esn) {
      setAuditLogEsn(request.esn);
    }
  }, [canViewAuditLog]);

  const onCloseAuditLog = useCallback(() => {
    setAuditLogEsn(null);
  }, []);

  const auditLogItem = useMemo((): VeScoutAuditItem => (
    auditLogEsn ? {esn: auditLogEsn} : {}
  ), [auditLogEsn]);

  const onViewAuditLogChanges = useCallback(function(auditRecord, show = false) {
    setAuditLogChangesRecord(show ? auditRecord : null);
  }, []);

  const onCloseAuditLogChanges = useCallback(() => {
    setAuditLogChangesRecord(null);
  }, []);

  const columnComponents = useMemo(() => ({
    rowMenu : ({data}) => (
      <CanRequestRowEditor request={data}
        onViewDetails={onViewDetails}
        onViewJson={onViewJson}
        onRetry={onRetry}
        onCancel={onCancel}
        onAuditLog={onShowAuditLog}
        canViewAuditLog={canViewAuditLog}
        retrying={retryingRequestId === data?.requestId}
        cancelling={cancellingRequestId === data?.requestId} />
    )
  }), [
    onViewDetails,
    onViewJson,
    onRetry,
    onCancel,
    onShowAuditLog,
    canViewAuditLog,
    retryingRequestId,
    cancellingRequestId
  ]);

  const onRowDoubleClicked = useCallback((event: any) => {
    onViewDetails?.(event?.data);
  }, [onViewDetails]);

  const onGridSelectionChanged = useCallback(() => {
    const api = gridApiRef.current;
    if (api) {
      const selectedNodes = api.getSelectedNodes().filter((node) => canRetryCanRequest(node?.data?.status));
      if (selectedNodes.length > maxSelection) {
        /*
         * Sorting is only needed to determine which of the over-the-cap nodes
         * to drop (the top-most by display order are kept), so it's skipped
         * entirely in the common case where nothing needs trimming.
         */
        selectedNodes
          .sort((left, right) => (left.rowIndex ?? 0) - (right.rowIndex ?? 0))
          .slice(maxSelection)
          .forEach((node) => node.setSelected(false));
      }
      onSelectionChange?.(selectedNodes.slice(0, maxSelection).map((node) => node.data));
    }
  }, [maxSelection, onSelectionChange]);

  /*
   * ag-grid only fires `onGridReady` once per grid instance, so the listener
   * it registers below can't be refreshed by re-running this callback when
   * `maxSelection`/`onSelectionChange` change — it would keep calling the
   * first render's closure forever. Routing the call through a ref that's
   * kept current on every render lets the one-time listener always reach
   * the latest `onGridSelectionChanged` logic.
   */
  const onGridSelectionChangedRef = useRef(onGridSelectionChanged);
  onGridSelectionChangedRef.current = onGridSelectionChanged;

  const onGridReady = useCallback((api: AgGridType) => {
    gridApiRef.current = api;
    api.addEventListener('selectionChanged', function() {
      onGridSelectionChangedRef.current();
    });
  }, []);

  const isRowSelectable = useCallback((node) => {
    const isAlreadySelected = !!node?.isSelected?.();
    return isAlreadySelected || (canRetryCanRequest(node?.data?.status) && selectedCount < maxSelection);
  }, [selectedCount, maxSelection]);

  const rowSelection: RowSelectionConfig = useMemo(() => ({
    mode           : 'multiRow',
    selectAll      : 'all',
    checkboxes     : selectionEnabled,
    headerCheckbox : selectionEnabled
  }), [selectionEnabled]);

  /*
   * The disabled-checkbox reason is applied as a plain `title` attribute on
   * hover rather than a colDef tooltipValueGetter, since it needs to reflect
   * live selectedCount/maxSelection state without forcing a column redefine.
   * The message itself doesn't interpolate `maxSelection`, since that value
   * is a per-device remaining-capacity number (e.g. `3`), not the fixed
   * pending-request ceiling (`MAX_PENDING_CAN_REQUESTS`) the message refers to.
   */
  const onSelectionCellMouseOver = useCallback((event: CellMouseOverEvent) => {
    if (event?.column?.getColId?.() === SELECTION_COLUMN_ID) {
      const cellElement = (event?.event?.target as HTMLElement)?.closest?.('.ag-cell') as HTMLElement | null;
      if (cellElement) {
        const isAlreadySelected = !!event?.node?.isSelected?.();
        const isAtCap           = selectedCount >= maxSelection;
        if (!isAlreadySelected && isAtCap && canRetryCanRequest(event?.data?.status)) {
          cellElement.setAttribute('title', translate(`${BULK_TRANSLATIONS}.maxSelectionTooltip`));
        } else {
          cellElement.removeAttribute('title');
        }
      }
    }
  }, [selectedCount, maxSelection]);

  const bulkRetryButton = useMemo(() => (
    <HeaderButton icon={<RetryIcon color={dlsColor('white')} />}
      label={translate(`${BULK_TRANSLATIONS}.retry`)}
      disabled={selectedCount === 0 || selectedCount > maxSelection || bulkRetrying}
      onClick={onBulkRetry} />
  ), [selectedCount, maxSelection, bulkRetrying, onBulkRetry]);

  return (
    <>
      <IonGrid ref={gridRef}
        isLoading={loading}
        onColumnChooserClose={function() { toggleColumnChooser(false); }}
        onColumnChooserSave={onColumnChooserSave}
        showColumnChooser={showColumnChooser}
        rowData={loadItems ? undefined : canRequestsData}
        totalRows={totalRows}
        rowModelType={rowModelType}
        defaultDefinitions={defaultDefinitions}
        checkboxesEnabled={selectionEnabled}
        isRowSelectable={selectionEnabled ? isRowSelectable : undefined}
        rowSelectionOverrides={selectionEnabled ? rowSelection : undefined}
        hideDisabledCheckboxes={!selectionEnabled}
        enableSelectAllCheckbox={selectionEnabled}
        showHeaderCheckbox={selectionEnabled}
        actionMenuItems={selectionEnabled && selectedCount > 1 ? bulkRetryButton : undefined}
        onCellMouseOver={selectionEnabled ? onSelectionCellMouseOver : undefined}
        getRowNodeId={getCanRequestRowId}
        onGridReady={onGridReady}
        columnDefinitions={columnDefinitions || definitions}
        columnComponents={columnComponents}
        augmentItems={constructGridRows}
        filterModel={filterModel}
        onColumnsUpdated={onColumnsUpdated}
        onFilterChanged={onFilterChanged}
        pageSizes={PAGE_SIZES}
        pageSize={gridPageSize}
        setGridPageSize={setGridPageSize}
        loadItems={loadItems}
        refreshRequired={refresh}
        maxBlocksInCache={1}
        onRowDoubleClicked={onRowDoubleClicked}
        omitHeader={selectionEnabled ? false : omitHeader}
        showCustomPaginationBar
        paginationEnabled
        rowMenusEnabled />
      <Render.If condition={!!auditLogEsn}>
        <AuditDialog rowMenusEnabled
          item={auditLogItem}
          entity={Entity.vescout}
          transformOptions={SCOUT_AUDIT_OPTIONS}
          onCloseDialog={onCloseAuditLog}
          onViewAuditChanges={onViewAuditLogChanges}
          showDifferences />
      </Render.If>
      <Render.If condition={!!auditLogChangesRecord}>
        <ViewAuditChangesDialog auditRecord={auditLogChangesRecord}
          entity={Entity.vescout}
          transformOptions={SCOUT_AUDIT_OPTIONS}
          onCloseDialog={onCloseAuditLogChanges} />
      </Render.If>
    </>
  );
}
);

const defaultColumn = (headerName: string, field: string): ColDef => ({
  headerName,
  field,
  resizable : true,
  sortable  : false,
  hide      : false
});

const getCommonColumnDefinitions = (standalone = false): ColDef[] => {
  const prefix = 'vehicle-manager:vehicleHistoryGrid.canRequestsGrid';
  const columns: ColDef[] = [
    defaultColumn(translate(`${prefix}.label`), 'label'),
    {
      ...defaultColumn(translate(`${prefix}.createdOn`), 'createdOn'),
      sortable : true,
      sort     : 'desc'
    },
    {
      ...defaultColumn(translate(`${prefix}.updatedOn`), 'updatedOn'),
      sortable : true
    },
    {
      ...defaultColumn(translate(`${prefix}.status`), 'status'),
      cellRenderer : statusCell
    }
  ];

  if (!standalone) {
    return columns;
  }

  return [
    ...columns,
    defaultColumn(translate(`${prefix}.esn`), 'esn'),
    defaultColumn(translate(`${prefix}.createdBy`), 'createdBy'),
    defaultColumn(translate(`${prefix}.requestId`), 'requestId')
  ];
};

const getColumnDefinitions = (standalone = false) => {
  const columns = resolveDefinitions(getCommonColumnDefinitions(standalone));
  return ({
    desktop : columns,
    mobile  : columns
  });
};

export default CanRequestsGrid;
